<template>
  <div class="user-management-panel">
    <div class="container pt-3 pb-5">
      <div class="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h1 class="h4 mb-1" data-cy="user-management-page-title">
            {{ $t("message.UserManagement.pageTitle") }}
          </h1>
          <p class="text-muted mb-0 small">{{ $t("message.UserManagement.pageSubtitle") }}</p>
        </div>
        <v-btn
          color="primary"
          data-cy="add-staff-user-btn"
          @click="openAdd"
        >
          {{ $t("message.UserManagement.addUser") }}
        </v-btn>
      </div>

      <LoadingState v-if="usersLoading" :text="$t('message.UserManagement.loading')" />

      <div v-else-if="!users.length" class="empty-state text-center py-10">
        <IconListEmptyState class="mb-3" />
        <div class="title fw-semibold">{{ $t("message.UserManagement.emptyTitle") }}</div>
        <p class="text-muted small">{{ $t("message.UserManagement.emptyMessage") }}</p>
      </div>

      <div v-else class="table-overflow-hide">
        <div class="directoryTable scrollable-55vh staff-directory-table">
          <table class="table" data-cy="staff-users-table">
            <thead class="sticky-header">
              <tr>
                <th scope="col">{{ $t("message.UserManagement.tableUsername") }}</th>
                <th scope="col">{{ $t("message.UserManagement.tableEmail") }}</th>
                <th
                  v-if="isDistrictCaller"
                  scope="col"
                  data-cy="table-col-school-name-header"
                >
                  {{ $t("message.UserManagement.typeSchool") }}
                </th>
                <th scope="col">{{ $t("message.UserManagement.tableRole") }}</th>
                <th class="icon-col text-end" scope="col">
                  <span class="visually-hidden">{{ $t("message.UserManagement.tableActions") }}</span>
                  <v-btn
                    icon
                    variant="text"
                    :loading="usersLoading"
                    :disabled="usersLoading"
                    data-cy="reload-staff-users-btn"
                    @click="fetchUsers()"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in users"
                :key="item.uid"
                class="table-row"
                :data-cy="`staff-user-row-${item.uid}`"
              >
                <td data-cy="table-col-username">
                  <v-btn
                    variant="text"
                    icon
                    color="gray600"
                    data-cy="staff-user-edit-pencil-btn"
                    :aria-label="$t('message.UserManagement.editUser')"
                    @click="openEdit(item)"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <span
                    class="staff-username-link"
                    data-cy="staff-user-username-btn"
                    role="button"
                    :aria-label="$t('message.UserManagement.editUser')"
                    @click="openEdit(item)"
                    @keydown.enter.prevent="openEdit(item)"
                    @keydown.space.prevent="openEdit(item)"
                  >
                    {{ item.username }}
                  </span>
                </td>
                <td data-cy="table-col-email">{{ item.email ?? "—" }}</td>
                <td
                  v-if="isDistrictCaller"
                  data-cy="table-col-school-name"
                >
                  {{ item.school_name ?? "—" }}
                </td>
                <td data-cy="table-col-role">
                  <div class="d-flex flex-wrap align-items-center gap-1">
                    <v-chip
                      v-if="getEffectiveRoleId(item) != null"
                      size="small"
                      :color="typeChipColor(item)"
                      :variant="isPrimaryAccountUser(item) ? 'elevated' : 'outlined'"
                      :class="canManageRow(item) ? 'staff-role-chip' : 'staff-role-chip-readonly'"
                      data-cy="staff-user-role-chip"
                      :tabindex="canManageRow(item) ? 0 : -1"
                      :role="canManageRow(item) ? 'button' : undefined"
                      :aria-label="
                        canManageRow(item) ? $t('message.UserManagement.changeRoleTitle') : undefined
                      "
                      @click="canManageRow(item) && openRoleQuick(item)"
                      @keydown.enter.prevent="canManageRow(item) && openRoleQuick(item)"
                      @keydown.space.prevent="canManageRow(item) && openRoleQuick(item)"
                    >
                      {{ roleLabelForRow(item) }}
                    </v-chip>
                    <span v-else class="text-muted small">{{ $t("message.UserManagement.roleNotSet") }}</span>
                  </div>
                </td>
                <td class="text-center">
                  <v-btn
                    v-if="canManageRow(item) && !isPrimaryAccountUser(item)"
                    color="gray300"
                    density="compact"
                    icon
                    variant="text"
                    data-cy="staff-user-delete-btn"
                    :aria-label="$t('message.UserManagement.deleteUserAria')"
                    @click="openDelete(item)"
                  >
                    <v-tooltip location="bottom" class="custom-tooltip" activator="parent">
                      {{ $t("message.UserManagement.deleteUserAria") }}
                    </v-tooltip>
                    <v-icon color="gray500">mdi-trash-can-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <SideModal
      :show="showAdd"
      location="right"
      close-on-overlay-click
      width="520px"
      @update:show="showAdd = $event"
    >
      <div class="modal-container">
        <div class="modal-top">
          <Typography color="primary" weight="600" size="1.25rem">{{
            $t("message.UserManagement.addUser")
          }}</Typography>
          <v-btn variant="plain" icon @click="showAdd = false" data-cy="staff-add-modal-close">
            <v-icon color="gray600">mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="modal-body staff-modal-form">
          <v-text-field
            v-model="addForm.username"
            class="custom-input"
            color="primary"
            variant="outlined"
            density="comfortable"
            :label="$t('message.UserManagement.formUsername')"
            data-cy="staff-add-username"
            autocomplete="off"
          />
          <v-text-field
            v-model="addForm.email"
            class="custom-input"
            color="primary"
            variant="outlined"
            density="comfortable"
            :label="$t('message.UserManagement.formEmail')"
            type="email"
            data-cy="staff-add-email"
          />
          <v-text-field
            v-model="addForm.password"
            class="custom-input"
            color="primary"
            variant="outlined"
            density="comfortable"
            :label="$t('message.UserManagement.formPassword')"
            type="password"
            data-cy="staff-add-password"
            autocomplete="new-password"
          />
          <v-select
            v-model="addForm.role_id"
            class="custom-input"
            color="primary"
            variant="outlined"
            density="comfortable"
            :label="$t('message.UserManagement.formRole')"
            :items="roleDefinitions"
            item-title="role_name"
            item-value="role_id"
            data-cy="staff-add-role-select"
            :menu-props="{ 'data-cy': 'staff-add-role-menu' } as any"
          />
          <v-select
            v-if="isDistrictCaller"
            v-model="addForm.school_id"
            class="custom-input"
            color="primary"
            variant="outlined"
            density="comfortable"
            :label="$t('message.UserManagement.formSchool')"
            :items="districtSchoolOptions"
            item-title="school_name"
            item-value="school_id"
            :loading="districtSchoolsLoading"
            :disabled="districtSchoolsLoading || !districtSchoolOptions.length"
            :hint="!districtSchoolOptions.length ? $t('message.UserManagement.schoolListEmpty') : undefined"
            persistent-hint
            data-cy="staff-add-school-select"
            :menu-props="{ 'data-cy': 'staff-add-school-menu' } as any"
          />
          <p class="text-caption text-medium-emphasis staff-modal-form-hint mb-0">
            {{ $t("message.UserManagement.loginHint") }}
          </p>
        </div>
        <div class="modal-footer d-flex gap-2 justify-end">
          <v-btn variant="text" data-cy="staff-add-cancel" @click="showAdd = false">{{
            $t("message.UserManagement.cancel")
          }}</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            data-cy="staff-add-save"
            @click="submitAdd"
          >
            {{ $t("message.UserManagement.save") }}
          </v-btn>
        </div>
      </div>
    </SideModal>

    <SideModal
      :show="showEdit"
      location="right"
      close-on-overlay-click
      width="520px"
      @update:show="showEdit = $event"
    >
      <div class="modal-container">
        <div class="modal-top">
          <Typography color="primary" weight="600" size="1.25rem">{{
            $t("message.UserManagement.editUser")
          }}</Typography>
          <v-btn variant="plain" icon @click="showEdit = false" data-cy="staff-edit-modal-close">
            <v-icon color="gray600">mdi-close</v-icon>
          </v-btn>
        </div>
        <div v-if="editUser" class="modal-body staff-modal-form">
          <v-text-field
            v-model="editForm.username"
            class="custom-input"
            color="primary"
            variant="outlined"
            density="comfortable"
            :label="$t('message.UserManagement.formUsername')"
            data-cy="staff-edit-username"
          />
          <v-text-field
            v-model="editForm.email"
            class="custom-input"
            color="primary"
            variant="outlined"
            density="comfortable"
            :label="$t('message.UserManagement.formEmail')"
            type="email"
            data-cy="staff-edit-email"
          />
          <v-text-field
            v-model="editForm.password"
            class="custom-input"
            color="primary"
            variant="outlined"
            density="comfortable"
            :label="$t('message.UserManagement.formNewPassword')"
            type="password"
            data-cy="staff-edit-password"
            autocomplete="new-password"
          />
          <v-select
            v-if="isDistrictCaller"
            v-model="editForm.school_id"
            class="custom-input"
            color="primary"
            variant="outlined"
            density="comfortable"
            :label="$t('message.UserManagement.formSchool')"
            :items="districtSchoolOptions"
            item-title="school_name"
            item-value="school_id"
            :loading="districtSchoolsLoading"
            :disabled="districtSchoolsLoading || !districtSchoolOptions.length"
            :hint="!districtSchoolOptions.length ? $t('message.UserManagement.schoolListEmpty') : undefined"
            persistent-hint
            data-cy="staff-edit-school-select"
            :menu-props="{ 'data-cy': 'staff-edit-school-menu' } as any"
          />
        </div>
        <div class="modal-footer d-flex gap-2 justify-end">
          <v-btn variant="text" data-cy="staff-edit-cancel" @click="showEdit = false">{{
            $t("message.UserManagement.cancel")
          }}</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            data-cy="staff-edit-save"
            @click="submitEdit"
          >
            {{ $t("message.UserManagement.save") }}
          </v-btn>
        </div>
      </div>
    </SideModal>

    <v-dialog v-model="showRoleQuick" max-width="420" data-cy="staff-role-quick-dialog">
      <v-card>
        <v-card-title>{{ $t("message.UserManagement.changeRoleTitle") }}</v-card-title>
        <v-card-text v-if="roleQuickUser" class="pb-2">
          <div class="text-body-2 text-medium-emphasis mb-3">
            Username: {{ roleQuickUser.username }}
          </div>
          <v-select
            v-model="roleQuickForm.role_id"
            class="custom-input"
            color="primary"
            variant="outlined"
            density="comfortable"
            :label="$t('message.UserManagement.formRole')"
            :items="roleDefinitions"
            item-title="role_name"
            item-value="role_id"
            data-cy="staff-role-quick-select"
            :menu-props="{ 'data-cy': 'staff-role-quick-menu' } as any"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-cy="staff-role-quick-cancel" @click="showRoleQuick = false">{{
            $t("message.UserManagement.cancel")
          }}</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            data-cy="staff-role-quick-save"
            @click="submitRoleQuick"
          >
            {{ $t("message.UserManagement.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDelete" max-width="480" data-cy="staff-delete-dialog">
      <v-card>
        <v-card-title>{{ $t("message.UserManagement.confirmDeleteTitle") }}</v-card-title>
        <v-card-text>
          {{ $t("message.UserManagement.confirmDeleteBody") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-cy="staff-delete-cancel" @click="showDelete = false">{{
            $t("message.UserManagement.cancel")
          }}</v-btn>
          <v-btn color="error" :loading="saving" data-cy="staff-delete-confirm" @click="submitDelete">
            {{ $t("message.UserManagement.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import SideModal from "@/components/sideModal.vue";
import Typography from "@/components/typography.vue";
import LoadingState from "@/components/loadingState.vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
import type {
  ApiRole,
  CreateStaffUserRequest,
  StaffUser,
  UpdateStaffUserRequest,
  UpdateUserRolesRequest
} from "@/api_types/schoolStaffUser";
import FetchService from "@/services/fetch.service";
import { authService } from "@/services/auth.service";
import { useAuthenticationStore } from "@/stores/authentication";
import { useAlertsStore } from "@/stores/alerts";
import { useDistrictStore } from "@/stores/district";
import { sort } from "@/utils/sort";
import type { GetSchoolDetails } from "@/api_types/school";

const STAFF_ROLE_ID_SCHOOL = 4;
const STAFF_ROLE_ID_REPORTER = 5;
const STAFF_ROLE_ID_DISTRICT = 6;
const STAFF_ROLE_ID_NOINTEGRATION = 8;

function toFiniteRoleId(value: unknown): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function getEffectiveRoleId(user: StaffUser): number | undefined {
  const raw = toFiniteRoleId(user.role_id);
  if (raw !== undefined) return raw;
  const nestedRole = user.roles?.[0] ?? user.user_roles?.[0];
  return toFiniteRoleId(nestedRole?.role_id);
}

function authTypeLower(user: StaffUser): string | undefined {
  return typeof user.authentication_type === "string"
    ? user.authentication_type.toLowerCase()
    : undefined;
}

function isDistrictStaff(user: StaffUser): boolean {
  if (getEffectiveRoleId(user) === STAFF_ROLE_ID_DISTRICT) return true;
  return authTypeLower(user) === "district";
}

const { t } = useI18n();
const authStore = useAuthenticationStore();
const alertsStore = useAlertsStore();
const districtStore = useDistrictStore();

/** Prefer decoding the access token so district vs school matches auth headers and e2e JWTs. */
function authenticationTypeFromAccessToken(): string | undefined {
  const token = authStore.getToken();
  if (!token?.access_token) return undefined;
  try {
    const p = authService.parseJwt(token.access_token) as { authentication_type?: string };
    const raw = p?.authentication_type;
    return typeof raw === "string" ? raw.toLowerCase() : undefined;
  } catch {
    return undefined;
  }
}

const isDistrictCaller = computed(() => authenticationTypeFromAccessToken() === "district");

const districtSchoolsLoading = ref(false);

const districtSchoolOptions = computed((): GetSchoolDetails[] => {
  const list = districtStore.schools ?? [];
  return sort([...list], (s) => s.school_name ?? "");
});

async function loadDistrictSchoolsIfNeeded(): Promise<void> {
  if (!isDistrictCaller.value) return;
  districtSchoolsLoading.value = true;
  try {
    await districtStore.fetchSchools();
  } finally {
    districtSchoolsLoading.value = false;
  }
}

const users = ref<StaffUser[]>([]);
const roleDefinitions = ref<ApiRole[]>([]);
const usersLoading = ref(false);
const primaryUid = computed(() => authStore.jwtData?.uid ?? "");

async function fetchUsers(): Promise<void> {
  usersLoading.value = true;
  try {
    const res = await FetchService.fetchData("/users", "get");
    users.value = res as StaffUser[];
  } finally {
    usersLoading.value = false;
  }
}

/** JWT `roles[0]` (e.g. `school`, `school.reporter`); if empty, map `authentication_type` like auth store fallback. */
function callerRoleNameForRolesQuery(): string {
  const roles = authStore.jwtData.roles;
  const jwtRoles = Array.isArray(roles) ? roles : [];
  if (jwtRoles.length > 0 && jwtRoles[0]) {
    return jwtRoles[0];
  }
  const byAuthType: Record<string, string> = {
    school: "school",
    district: "district",
    support: "admin",
    teacher: "teacher",
    parent: "parent"
  };
  return byAuthType[authStore.jwtData.authentication_type] ?? "school";
}

async function fetchRoleDefinitions(): Promise<void> {
  const params = new URLSearchParams({ type: callerRoleNameForRolesQuery() });
  const res = await FetchService.fetchData(`/roles?${params.toString()}`, "get");
  roleDefinitions.value = Array.isArray(res) ? (res as ApiRole[]) : [];
}

async function createStaffUser(
  body: CreateStaffUserRequest
): Promise<{ ok: true; uid: string } | { ok: false; message?: string }> {
  const res = await FetchService.fetchData("/users", "post", body);
  if (res === false) {
    return { ok: false };
  }
  if (res.uid && typeof res.uid === "string" && res.uid.length > 0 && res.status) {
    return { ok: true, uid: res.uid};
  }
  const msg =
    res && typeof res === "object" && "message" in res
      ? String((res as { message?: string }).message || "")
      : "";
  return { ok: false, message: msg || undefined };
}

async function updateUser(uid: string, body: UpdateStaffUserRequest): Promise<boolean> {
  const res = await FetchService.fetchData(`/users/${uid}`, "patch", body);
  return res !== false && res != null;
}

async function deleteUser(uid: string): Promise<boolean> {
  const res = await FetchService.fetchData(`/users/${uid}`, "delete");
  return res !== false;
}

async function updateUserRoles(uid: string, body: UpdateUserRolesRequest): Promise<boolean> {
  const res = await FetchService.fetchData(`/users/${uid}/roles`, "patch", body);
  return res !== false && res != null;
}

const showAdd = ref(false);
const showEdit = ref(false);
const showDelete = ref(false);
const showRoleQuick = ref(false);
const saving = ref(false);
const editUser = ref<StaffUser | null>(null);
const deleteTarget = ref<StaffUser | null>(null);
const roleQuickUser = ref<StaffUser | null>(null);

const addForm = reactive({
  username: "",
  email: "",
  password: "",
  role_id: STAFF_ROLE_ID_SCHOOL,
  school_id: null as number | null
});

const editForm = reactive({
  username: "",
  email: "",
  password: "",
  school_id: null as number | null
});

const roleQuickForm = reactive({
  role_id: STAFF_ROLE_ID_SCHOOL
});

const assignableRoleIds = computed(() =>
  roleDefinitions.value.map((r) => Number(r.role_id))
);

function apiRoleLabel(roleId: number): string {
  const def = roleDefinitions.value.find((r) => Number(r.role_id) === Number(roleId));
  const raw = (def?.role_name ?? def?.name ?? "").trim();
  return raw || t("message.UserManagement.roleUnknown", { id: roleId });
}

function roleLabelForRow(item: StaffUser): string {
  const nested = item.roles?.[0]?.name;
  if (nested != null && String(nested).trim()) {
    return String(nested).trim();
  }
  const rid = getEffectiveRoleId(item);
  return rid != null ? apiRoleLabel(rid) : "";
}

watch(showRoleQuick, (open) => {
  if (!open) {
    roleQuickUser.value = null;
  }
});

watch(showAdd, async (open) => {
  if (open) {
    addForm.username = "";
    addForm.email = "";
    addForm.password = "";
    addForm.school_id = null;
    const ids = assignableRoleIds.value;
    addForm.role_id = ids.includes(STAFF_ROLE_ID_SCHOOL)
      ? STAFF_ROLE_ID_SCHOOL
      : (ids[0] ?? STAFF_ROLE_ID_SCHOOL);
    if (isDistrictCaller.value) {
      await loadDistrictSchoolsIfNeeded();
      const first = districtSchoolOptions.value[0]?.school_id;
      addForm.school_id = first != null ? Number(first) : null;
    }
  }
});

function typeChipColor(item: StaffUser): string {
  if (isDistrictStaff(item)) return "secondary";
  const rid = getEffectiveRoleId(item);
  if (rid === STAFF_ROLE_ID_REPORTER) return "info";
  return "primary";
}

function canManageRow(item: StaffUser): boolean {
  if (isPrimaryAccountUser(item)) return false;
  if (isDistrictCaller.value) return true;
  return !isDistrictStaff(item);
}

function openAdd() {
  showAdd.value = true;
}

function isPrimaryAccountUser(item: StaffUser): boolean {
  return !!primaryUid.value && item.uid === primaryUid.value;
}

async function openEdit(item: StaffUser) {
  editUser.value = item;
  editForm.username = item.username ?? "";
  editForm.email = item.email ?? "";
  editForm.password = "";
  const sid = item.school_id;
  editForm.school_id =
    sid !== undefined && sid !== null && sid !== ""
      ? Number(sid)
      : null;
  showEdit.value = true;
  if (isDistrictCaller.value) {
    await loadDistrictSchoolsIfNeeded();
  }
}

function openRoleQuick(item: StaffUser) {
  roleQuickUser.value = item;
  const rid = getEffectiveRoleId(item);
  const allowed = assignableRoleIds.value;
  roleQuickForm.role_id =
    rid != null && allowed.includes(rid)
      ? rid
      : allowed.includes(STAFF_ROLE_ID_SCHOOL)
        ? STAFF_ROLE_ID_SCHOOL
        : (allowed[0] ?? STAFF_ROLE_ID_SCHOOL);
  showRoleQuick.value = true;
}

function openDelete(item: StaffUser) {
  if (isPrimaryAccountUser(item)) return;
  deleteTarget.value = item;
  showDelete.value = true;
}

async function submitAdd() {
  if (!addForm.username.trim() || !addForm.email.trim() || !addForm.password) {
    alertsStore.addAlert("error", t("message.UserManagement.validationRequired"));
    return;
  }
  if (isDistrictCaller.value) {
    await loadDistrictSchoolsIfNeeded();
    if (addForm.school_id == null && districtSchoolOptions.value[0]?.school_id != null) {
      addForm.school_id = Number(districtSchoolOptions.value[0].school_id);
    }
    if (addForm.school_id == null || !Number.isFinite(addForm.school_id)) {
      alertsStore.addAlert("error", t("message.UserManagement.validationSchoolRequired"));
      return;
    }
  }
  saving.value = true;
  try {
    const body: CreateStaffUserRequest = {
      username: addForm.username.trim(),
      email: addForm.email.trim(),
      password: addForm.password,
      role_id: addForm.role_id
    };
    if (isDistrictCaller.value && addForm.school_id != null) {
      body.school_id = addForm.school_id;
    }
    const res = await createStaffUser(body);
    if (res.ok) {
      alertsStore.addAlert("success", t("message.UserManagement.createdSuccess"));
      showAdd.value = false;
      await fetchUsers();
    } else {
      alertsStore.addAlert(
        "error",
        res.message?.trim() || t("message.UserManagement.genericError")
      );
    }
  } finally {
    saving.value = false;
  }
}

async function submitEdit() {
  if (!editUser.value) return;
  if (!editForm.username.trim() || !editForm.email.trim()) {
    alertsStore.addAlert("error", t("message.UserManagement.validationRequired"));
    return;
  }
  if (isDistrictCaller.value) {
    await loadDistrictSchoolsIfNeeded();
    if (editForm.school_id == null && districtSchoolOptions.value[0]?.school_id != null) {
      editForm.school_id = Number(districtSchoolOptions.value[0].school_id);
    }
    if (editForm.school_id == null || !Number.isFinite(editForm.school_id)) {
      alertsStore.addAlert("error", t("message.UserManagement.validationSchoolRequired"));
      return;
    }
  }
  saving.value = true;
  try {
    const uid = editUser.value.uid;
    const patchBody: UpdateStaffUserRequest = {
      username: editForm.username.trim(),
      email: editForm.email.trim()
    };
    if (editForm.password.trim()) {
      patchBody.password = editForm.password;
    }
    if (isDistrictCaller.value && editForm.school_id != null) {
      patchBody.school_id = editForm.school_id;
    }
    const profileOk = await updateUser(uid, patchBody);
    if (profileOk) {
      alertsStore.addAlert("success", t("message.UserManagement.updatedSuccess"));
      showEdit.value = false;
      await fetchUsers();
    } else {
      alertsStore.addAlert("error", t("message.UserManagement.genericError"));
    }
  } finally {
    saving.value = false;
  }
}

async function submitRoleQuick() {
  if (!roleQuickUser.value) return;
  saving.value = true;
  try {
    const uid = roleQuickUser.value.uid;
    const prev =
      getEffectiveRoleId(roleQuickUser.value) ??
      (isDistrictStaff(roleQuickUser.value) ? STAFF_ROLE_ID_DISTRICT : STAFF_ROLE_ID_SCHOOL);
    if (roleQuickForm.role_id === prev) {
      showRoleQuick.value = false;
      return;
    }
    const ok = await updateUserRoles(uid, { role_id: roleQuickForm.role_id });
    if (ok) {
      alertsStore.addAlert("success", t("message.UserManagement.roleUpdatedSuccess"));
      showRoleQuick.value = false;
      await fetchUsers();
    } else {
      alertsStore.addAlert("error", t("message.UserManagement.genericError"));
    }
  } finally {
    saving.value = false;
  }
}

async function submitDelete() {
  if (!deleteTarget.value) return;
  saving.value = true;
  try {
    const ok = await deleteUser(deleteTarget.value.uid);
    if (ok) {
      alertsStore.addAlert("success", t("message.UserManagement.deletedSuccess"));
      showDelete.value = false;
      deleteTarget.value = null;
      await fetchUsers();
    } else {
      alertsStore.addAlert("error", t("message.UserManagement.genericError"));
    }
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  await Promise.all([fetchUsers(), fetchRoleDefinitions()]);
  if (isDistrictCaller.value) {
    await loadDistrictSchoolsIfNeeded();
  }
});
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;

.user-management-panel {
  .empty-state .title {
    font-size: 1.1rem;
  }

  /**
   * Global `.directoryTable` targets directory pin + name columns (28px / 250px).
   * Staff users table uses full-width columns instead.
   */
  .staff-directory-table.directoryTable table tbody td {
    width: auto !important;
  }

  .staff-username-link {
    cursor: pointer;
    color: $gray-800;
    border-radius: 2px;
  }

  .staff-username-link:hover {
    color: $primary;
  }

  .staff-username-link:focus-visible {
    outline: 2px solid $primary;
    outline-offset: 2px;
  }

  .staff-role-chip {
    cursor: pointer;
  }

  .staff-role-chip:focus-visible {
    outline: 2px solid $primary;
    outline-offset: 2px;
  }

  .staff-role-chip-readonly {
    cursor: default;
  }

  .modal-container {
    padding: 1rem 1.25rem 1.5rem;
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  .modal-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  /** Do not grow the body to fill the slide-in — that was stretching inputs vertically. */
  .modal-body {
    flex: 0 1 auto;
  }

  /** Row spacing only; grid avoids flex stretch on v-input children. */
  .staff-modal-form {
    display: grid;
    gap: 0.75rem;
  }

  .staff-modal-form-hint {
    margin-top: 0;
  }

  .modal-footer {
    margin-top: 1rem;
    padding-top: 0.5rem;
  }
}
</style>
